/**
 * React Query hooks for Notifications API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/api/services/notifications.service';
import type {
  Notification,
  NotificationCreate,
  BulkNotificationCreate,
  NotificationCount,
  BulkNotificationOperation,
  Subscription,
  SubscriptionCreate,
  SubscriptionUpdate,
  Preference,
  PreferenceCreate,
  UserOption,
  OptionCreate,
  OptionUpdate,
  NotificationQueryParams,
} from '@/api/types/notification.types';

/**
 * Query key factory for notifications-related queries
 */
export const notificationsKeys = {
  all: ['notifications'] as const,
  list: (params?: NotificationQueryParams) =>
    [...notificationsKeys.all, 'list', params] as const,
  unread: () => [...notificationsKeys.all, 'unread'] as const,
  count: () => [...notificationsKeys.all, 'count'] as const,
  detail: (id: string) => [...notificationsKeys.all, 'detail', id] as const,
  subscriptions: () => [...notificationsKeys.all, 'subscriptions'] as const,
  subscriptionsAvailable: () =>
    [...notificationsKeys.all, 'subscriptions', 'available'] as const,
  preferences: () => [...notificationsKeys.all, 'preferences'] as const,
  userOption: (key: string) =>
    [...notificationsKeys.all, 'options', key] as const,
};

// ============================================================================
// NOTIFICATIONS
// ============================================================================

/**
 * Hook to fetch notifications with optional filtering
 */
export const useNotifications = (params?: NotificationQueryParams) => {
  return useQuery({
    queryKey: notificationsKeys.list(params),
    queryFn: () => notificationsApi.getNotifications(params),
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch only unread notifications
 */
export const useUnreadNotifications = (params?: {
  limit?: number;
  offset?: number;
}) => {
  return useQuery({
    queryKey: notificationsKeys.unread(),
    queryFn: () => notificationsApi.getUnreadNotifications(params),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Poll every 60 seconds as fallback
  });
};

/**
 * Hook to fetch notification counts
 */
export const useNotificationCount = () => {
  return useQuery<NotificationCount, Error>({
    queryKey: notificationsKeys.count(),
    queryFn: notificationsApi.getNotificationCount,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Poll every 30 seconds
  });
};

/**
 * Hook to fetch a single notification by ID
 */
export const useNotification = (id: string) => {
  return useQuery({
    queryKey: notificationsKeys.detail(id),
    queryFn: () => notificationsApi.getNotificationById(id),
    enabled: !!id, // Only fetch if id is provided
  });
};

/**
 * Hook to create a new notification
 */
export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NotificationCreate) =>
      notificationsApi.createNotification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to mark notification as seen
 */
export const useMarkAsSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsApi.markAsSeen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to mark notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to archive notification
 */
export const useArchiveNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsApi.archiveNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to delete notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsApi.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

// ============================================================================
// BULK OPERATIONS
// ============================================================================

/**
 * Hook to create bulk notifications
 */
export const useCreateBulkNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkNotificationCreate) =>
      notificationsApi.createBulkNotifications(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to bulk mark as read
 */
export const useBulkMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkNotificationOperation) =>
      notificationsApi.bulkMarkAsRead(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to bulk archive
 */
export const useBulkArchive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkNotificationOperation) =>
      notificationsApi.bulkArchive(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to bulk delete
 */
export const useBulkDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkNotificationOperation) =>
      notificationsApi.bulkDelete(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to delete all notifications
 */
export const useDeleteAllNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsApi.deleteAllNotifications(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

/**
 * Hook to fetch user subscriptions
 */
export const useSubscriptions = () => {
  return useQuery<Subscription[], Error>({
    queryKey: notificationsKeys.subscriptions(),
    queryFn: notificationsApi.getSubscriptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch available subscription types
 */
export const useAvailableSubscriptions = () => {
  return useQuery({
    queryKey: notificationsKeys.subscriptionsAvailable(),
    queryFn: notificationsApi.getAvailableSubscriptions,
    staleTime: 10 * 60 * 1000, // 10 minutes (rarely changes)
  });
};

/**
 * Hook to create subscription
 */
export const useCreateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubscriptionCreate) =>
      notificationsApi.createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.subscriptions(),
      });
    },
  });
};

/**
 * Hook to update subscription
 */
export const useUpdateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SubscriptionUpdate }) =>
      notificationsApi.updateSubscription(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.subscriptions(),
      });
    },
  });
};

/**
 * Hook to delete subscription
 */
export const useDeleteSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsApi.deleteSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.subscriptions(),
      });
    },
  });
};

/**
 * Hook to reset subscriptions to defaults
 */
export const useResetSubscriptions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsApi.resetSubscriptionsToDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.subscriptions(),
      });
    },
  });
};

// ============================================================================
// PREFERENCES
// ============================================================================

/**
 * Hook to fetch channel preferences
 */
export const usePreferences = () => {
  return useQuery<Preference[], Error>({
    queryKey: notificationsKeys.preferences(),
    queryFn: notificationsApi.getPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch channel preferences (alias for usePreferences)
 * Preferences represent per-channel settings
 */
export const useChannelPreferences = usePreferences;

/**
 * Hook to update preference (upsert)
 */
export const useUpdatePreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PreferenceCreate) =>
      notificationsApi.updatePreference(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.preferences(),
      });
    },
  });
};

/**
 * Hook to update channel preference by ID (alias for useUpdatePreference)
 */
export const useUpdateChannelPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preferenceId,
      data,
    }: {
      preferenceId: string;
      data: PreferenceCreate;
    }) => notificationsApi.updatePreference(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.preferences(),
      });
    },
  });
};

// ============================================================================
// USER OPTIONS
// ============================================================================

/**
 * Hook to fetch user option
 */
export const useUserOption = (key: string) => {
  return useQuery<UserOption, Error>({
    queryKey: notificationsKeys.userOption(key),
    queryFn: () => notificationsApi.getUserOption(key),
    enabled: !!key,
  });
};

/**
 * Hook to create user option
 */
export const useCreateUserOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OptionCreate) =>
      notificationsApi.createUserOption(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to update user option
 */
export const useUpdateUserOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: OptionUpdate }) =>
      notificationsApi.updateUserOption(key, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.userOption(variables.key),
      });
    },
  });
};

/**
 * Hook to delete user option
 */
export const useDeleteUserOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => notificationsApi.deleteUserOption(key),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({
        queryKey: notificationsKeys.userOption(key),
      });
    },
  });
};

/**
 * Hook to fetch all user options (aggregated)
 * TODO: Backend endpoint not yet implemented (mock data)
 */
export const useUserOptions = () => {
  return useQuery({
    queryKey: ['notifications', 'options', 'all'],
    queryFn: notificationsApi.getUserOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to update all user options (batch update)
 * TODO: Backend endpoint not yet implemented (mock mutation)
 */
export const useUpdateUserOptions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      sound_enabled?: boolean;
      digest_frequency?: string;
      quiet_hours_start?: string | null;
      quiet_hours_end?: string | null;
    }) => notificationsApi.updateUserOptions(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'options', 'all'],
      });
    },
  });
};
